import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setStatus(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-fetch every 60s while user is logged in (catches mid-session lock changes
  // such as cron-triggered suspension).
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [user, refresh]);

  // Auto-recover: 402 on any other API call → re-fetch status so banner appears.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) refresh();
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(interceptor);
  }, [refresh]);

  const value = useMemo(
    () => ({ status, loading, blocked: !!status?.blocked, refresh }),
    [status, loading, refresh],
  );
  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export const useBilling = () => useContext(BillingContext);
